import { Button } from '@/components/shadcn/button'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconDocumentExportOutline } from '@icons/IconDocumentExportOutline'
import { Table } from '@tanstack/react-table'
import { BulkAction } from '../types-v4'
import { exportToExcel } from '../utils/exportUtils'

interface BulkActionsProps<TData> {
  actions?: BulkAction[]
  table: Table<TData>
  data: TData[]
  title?: string
}

export function BulkActions<TData>({ actions, table, data, title }: BulkActionsProps<TData>) {
  if (!actions) return null

  const handleExport = async (requiresSelection: boolean) => {
    const date = new Date().toLocaleDateString()
    const fileName = title ? `${title} - ${date}.xlsx` : `export - ${date}.xlsx`
    let exportData = data

    // Flatten the data if it's grouped (contains subRows)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flattenData = (items: any[]) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return items.reduce((acc: any[], item: any) => {
        // If item is a group (has subRows), add the subRows (children) instead of the group row itself
        // You might want to include the group row too, but usually exports want the raw data rows
        if (item.subRows && Array.isArray(item.subRows)) {
          // Recursive call in case of multi-level grouping, though currently we only have 1 level
          acc.push(...flattenData(item.subRows))
        } else {
          acc.push(item)
        }
        return acc
      }, [])
    }

    if (requiresSelection) {
      const selectedRows = table.getSelectedRowModel().rows
      const selectedOriginals = selectedRows.map((row) => row.original)
      exportData = flattenData(selectedOriginals)

      if (exportData.length === 0) {
        // Optionally handle no selection case
        return
      }
    } else {
      // If exporting all data, we need to flatten the grouped data structure
      // 'data' prop passed here is likely the grouped structure returned by useTableDataGrouping
      exportData = flattenData(data)
    }

    await exportToExcel(exportData as Record<string, unknown>[], fileName)
  }

  return (
    <>
      {actions.map((action) => {
        if (!action.visible) return null
        if (action.type === 'export_xlsx') {
          const isDisabled = action.requiresSelection && Object.keys(table.getState().rowSelection).length === 0
          if (isDisabled) {
            return null
          }
          return (
            <TooltipButton tooltip={action.label || 'Export to Excel'}>
              <Button
                size="icon"
                variant="outline"
                className="size-8 rounded-full"
                onClick={() => handleExport(action.requiresSelection)}
              >
                <IconDocumentExportOutline className="size-4" />
              </Button>
            </TooltipButton>
          )
        }
        return null
      })}
    </>
  )
}
